import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';
import { decodeProductId, encodeProductId } from '@/lib/product-url-codec';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHero } from '@/components/layout/PageHero';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { AmendmentBanner } from '@/features/product/components/AmendmentBanner';
import { CountryClassificationCard } from '@/features/product/components/CountryClassificationCard';
import { ProductNameCard } from '@/features/product/components/ProductNameCard';
import { ProductUnavailableNotice } from '@/features/product/components/ProductUnavailableNotice';
import { ApiError } from '@/lib/api-client';
import {
  SUPPORTING_DOCS_SECTION_ID,
  clearSupportingDocsFocus,
  consumeSupportingDocsFocus,
} from '@/features/product/dispatch-gate';
import { useProductForEdit } from '@/features/product/hooks';
import { SupportingDocumentsUploader } from '@/features/product/components/SupportingDocumentsUploader';
import { StructuredSpecView } from '@/features/tech-spec-extraction/section-review/StructuredSpecView';
import { DoneButton } from '@/features/strategy/components/DoneButton';

/**
 * The product page: where every product is edited (every Edit button lands here).
 *
 * Shows the reviewed technical specification in sections (``StructuredSpecView``), or, while an
 * upload's sections still await approval, a link to that review. The old editors (the 10-section
 * wizard and the key:value ``DynamicJsonEditor``) are retired: legacy products were converted to
 * sections (``backend/services/legacy_spec_backfill``). A legacy product that couldn't be (no
 * stored document text to build sections from) shows a notice instead of an editor.
 */

const paramsSchema = z.object({
  productId: z.coerce.number().int().positive(),
});

export const Route = createFileRoute('/product-account_/$productId_/edit')({
  params: {
    // Opaque token in the address bar, never the global product id; a legacy
    // numeric link still resolves.
    parse: ({ productId }) =>
      paramsSchema.parse({ productId: decodeProductId(productId) ?? productId }),
    stringify: ({ productId }) => ({ productId: encodeProductId(productId) }),
  },
  component: ProductDynamicEditPage,
});

export function ProductDynamicEditPage() {
  const { productId } = Route.useParams();
  const navigate = useNavigate();
  // The agents' "Check documents" button lands on the documents list, not the top of
  // the page. The intent arrives in memory (no URL hash — see dispatch-gate.ts) and is
  // consumed once here. The section renders after the product loads, so retry briefly.
  useEffect(() => {
    if (!consumeSupportingDocsFocus()) return;
    let tries = 0;
    let observer: ResizeObserver | null = null;
    let mutations: MutationObserver | null = null;
    let settle: number | null = null;
    let hardStop: number | null = null;
    let done = false;
    let userScrolled: (() => void) | null = null;
    let scrollHost: HTMLElement | null = null;
    // 'nearest': scroll just far enough for the list to be in view. Pinning it to
    // the top ('start') left the page hanging over empty space below the Done button.
    // Scroll ONLY the app's content pane. `scrollIntoView` would also scroll every
    // ancestor including the window, which dragged the page down past the shell and
    // left the section hanging over empty space (seen on a tall display).
    const paneFor = (el: HTMLElement): HTMLElement | null => {
      let pane: HTMLElement | null = el.parentElement;
      while (pane && pane !== document.body) {
        const o = getComputedStyle(pane).overflowY;
        if (o === 'auto' || o === 'scroll') break;
        pane = pane.parentElement;
      }
      return pane && pane !== document.body ? pane : null;
    };
    const reveal = (el: HTMLElement, behavior: ScrollBehavior) => {
      const pane = paneFor(el);
      if (!pane) return;
      const r = el.getBoundingClientRect();
      const p = pane.getBoundingClientRect();
      const margin = 24;
      // Just far enough: bring the bottom into view, but never push the heading above the top.
      const overflowBelow = r.bottom - (p.bottom - margin);
      const headingAbove = r.top - (p.top + margin);
      const delta = overflowBelow > 0 ? Math.min(overflowBelow, headingAbove) : headingAbove < 0 ? headingAbove : 0;
      if (delta !== 0) pane.scrollBy({ top: delta, behavior });
    };
    const tick = () => {
      const el = document.getElementById(SUPPORTING_DOCS_SECTION_ID);
      if (!el) {
        if (tries++ < 100) window.setTimeout(tick, 100);
        return;
      }
      const pane = paneFor(el);
      reveal(el, 'smooth');
      if (!pane) return;
      scrollHost = pane;

      // Stop following once the page has held still briefly — the page decides when it
      // is finished loading, not a guessed timeout. Anything that moves the section
      // restarts the wait; the hard cap below means it can never run on forever.
      const finish = () => {
        if (done) return;
        done = true;
        // Honoured: a later visit to this page must not scroll again. Deliberately NOT in
        // the effect's cleanup — StrictMode runs that between its two dev mounts, and
        // clearing there is precisely what made this fail at random.
        clearSupportingDocsFocus();
        observer?.disconnect();
        mutations?.disconnect();
        if (settle != null) window.clearTimeout(settle);
        if (hardStop != null) window.clearTimeout(hardStop);
        if (userScrolled && scrollHost) scrollHost.removeEventListener('wheel', userScrolled);
        if (userScrolled && scrollHost) scrollHost.removeEventListener('touchmove', userScrolled);
      };
      // The user taking over wins immediately: chasing the section while someone is
      // trying to read something else is worse than never having scrolled.
      userScrolled = finish;
      pane.addEventListener('wheel', userScrolled, { passive: true });
      pane.addEventListener('touchmove', userScrolled, { passive: true });

      const bump = () => {
        if (done) return;
        reveal(el, 'auto');
        if (settle != null) window.clearTimeout(settle);
        settle = window.setTimeout(finish, 600);
      };

      // Size changes: the section's own list arriving, or any block above it growing.
      observer = new ResizeObserver(bump);
      observer.observe(el);
      observer.observe(pane);
      for (const child of Array.from(pane.children)) observer.observe(child);
      // Structure changes: a block INSERTED above the section later (the spec editor
      // mounting) never resizes an element we already knew about, so size alone missed
      // it. Re-observe new children as they appear.
      mutations = new MutationObserver((records) => {
        for (const rec of records) {
          for (const node of Array.from(rec.addedNodes)) {
            if (node instanceof HTMLElement) observer?.observe(node);
          }
        }
        bump();
      });
      mutations.observe(pane, { childList: true, subtree: true });

      settle = window.setTimeout(finish, 600);
      hardStop = window.setTimeout(finish, 10_000);
    };
    tick();
    return () => {
      done = true;
      observer?.disconnect();
      mutations?.disconnect();
      if (settle != null) window.clearTimeout(settle);
      if (hardStop != null) window.clearTimeout(hardStop);
      if (userScrolled && scrollHost) {
        scrollHost.removeEventListener('wheel', userScrolled);
        scrollHost.removeEventListener('touchmove', userScrolled);
      }
    };
  }, []);

  // Section review (the pivot): a section-approved product carries its reviewed spec
  // as markdown; the section view shows and edits it.
  // ONE snapshot drives the whole page (the name and classification cards share its query key).
  const snapshot = useProductForEdit(productId);
  const structuredMarkdown =
    snapshot.data && snapshot.data.tech_spec_markdown != null
      ? snapshot.data.tech_spec_markdown
      : null;
  // Everything the view-gate needs rides on the SINGLE product-edit snapshot now — no separate,
  // source_markdown-heavy extraction-proposal fetch. Only one query to wait on, so the loading
  // gate stays simple and the old "Add a field" editor can't flash during a cold load.
  const loading = snapshot.isPending;
  // Archived or deleted under the user (a teammate, another tab). The snapshot polls, so this
  // flips on its own; the editors are then replaced by a notice rather than left live with
  // every save doomed to a 409/404. `status` has been on the snapshot for exactly this — the
  // schema comment says "lets the editor refuse to render a UI for an already-archived row" —
  // but nothing read it until now.
  const unavailable: 'archived' | 'deleted' | null =
    snapshot.isError && snapshot.error instanceof ApiError && snapshot.error.status === 404
      ? 'deleted'
      : snapshot.data?.status === 'archived'
        ? 'archived'
        : null;
  // A product whose spec isn't approved yet: send the reviewer to approve the sections first.
  // A legacy product the conversion left (no section text, not in review) gets the
  // older-format notice.
  const inReview = Boolean(
    structuredMarkdown == null &&
      snapshot.data?.extraction_status != null &&
      snapshot.data.extraction_status !== 'approved',
  );

  return (
    <>
      <PageHero
        title="Edit Product Data"
        subtitle={
          loading ? (
            <>Loading your product&hellip;</>
          ) : unavailable === 'archived' ? (
            <>This product has been archived and can no longer be edited.</>
          ) : unavailable === 'deleted' ? (
            <>This product no longer exists.</>
          ) : structuredMarkdown != null ? (
            <>
              This is your product&rsquo;s reviewed technical specification &mdash; the source
              of truth the REGARA AI agents read when analyzing it. Edit any section or add new
              ones; changes save automatically. You can also add supporting documents below.
            </>
          ) : inReview ? (
            <>
              Your technical specification is still in review. Approve the sections we read from
              your upload first &mdash; then your reviewed spec becomes editable here.
            </>
          ) : (
            <>
              This product&rsquo;s technical specification is in an older format that can no
              longer be edited here.
            </>
          )
        }
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20 space-y-6">
        <Button variant="ghost" asChild className="text-navy -ml-3 self-start">
          <Link to="/product-account">
            <ArrowLeft className="size-3.5" /> Back to products
          </Link>
        </Button>
        {/* Nothing below the notice may invite an edit once the product is gone. */}
        {!unavailable && <AmendmentBanner />}
        {loading ? (
          <div className="bg-white border border-line rounded-2xl p-6 shadow-card">
            <div className="h-6 w-56 bg-line/60 rounded animate-pulse mb-4" />
            <div className="h-28 bg-line/40 rounded animate-pulse" />
          </div>
        ) : unavailable ? (
          <ProductUnavailableNotice kind={unavailable} />
        ) : structuredMarkdown != null ? (
          <>
            <ProductNameCard productId={productId} />
            <CountryClassificationCard productId={productId} />
            <StructuredSpecView
              key={productId}
              productId={productId}
              markdown={structuredMarkdown}
              updatedAt={snapshot.data!.updated_at}
            />
          </>
        ) : inReview ? (
          <>
            <ProductNameCard productId={productId} />
            <CountryClassificationCard productId={productId} />
            <section className="bg-white border border-line rounded-2xl p-8 shadow-card text-center">
              <h2 className="text-navy text-[18px] font-bold m-0 mb-2">
                Your technical specification is still in review
              </h2>
              <p className="text-muted text-[14px] leading-relaxed m-0 mb-5 max-w-[560px] mx-auto">
                Review the sections we read from your upload and approve them. Once approved,
                your reviewed spec appears here as the source of truth the REGARA AI agents read.
              </p>
              <Button asChild>
                <Link to="/product-account/$productId/review" params={{ productId }}>
                  Review &amp; approve the spec
                </Link>
              </Button>
            </section>
          </>
        ) : (
          // A legacy product the conversion couldn't turn into sections (no stored document
          // text). The key:value editor is retired, so there is nothing to edit it with.
          <>
            <ProductNameCard productId={productId} />
            <CountryClassificationCard productId={productId} />
            <section className="bg-white border border-line rounded-2xl p-8 shadow-card text-center">
              <h2 className="text-navy text-[18px] font-bold m-0 mb-2">
                This technical specification is in an older format
              </h2>
              <p className="text-muted text-[14px] leading-relaxed m-0 max-w-[560px] mx-auto">
                It was added before section review and couldn&rsquo;t be converted, so it can&rsquo;t
                be edited here. To keep working on it, upload its technical specification as a new
                product and review it section by section.
              </p>
            </section>
          </>
        )}
        {!unavailable && (
          <>
        <section
          id={SUPPORTING_DOCS_SECTION_ID}
          className="scroll-mt-6 bg-white border border-line rounded-2xl p-6 shadow-card"
        >
          <h2 className="text-navy text-[18px] font-bold m-0 mb-3">
            Supporting Documents
          </h2>
          <SupportingDocumentsUploader productId={productId} />
        </section>
        <div className="flex justify-end">
          <DoneButton
            productId={productId}
            onDone={() => void navigate({ to: '/product-account' })}
          />
        </div>
          </>
        )}
      </main>
      <ScrollToTopButton />
    </>
  );
}
